import React, { useState } from 'react';
import { FileUpload, TermsDocument } from 'common/types';
import {
	Box,
	Typography,
	IconButton,
	Tooltip,
	List,
	ListItem,
	ListItemText,
	ListItemAvatar,
	ListItemSecondaryAction,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import ClearIcon from '@mui/icons-material/Clear';
import classNames from 'classnames';
import StripePattern from 'common/components/StripePattern';
import { useTranslation } from 'services/localization/useTranslation';

interface Props {
	file?: FileUpload | TermsDocument;
	onFileSelected: (file: File, fileName: string) => void;
	onFileRemoved?: () => void;
	onError: (error: string) => void;
	isUploading: boolean;
	disabled?: boolean;
}

/** Make this into a prop when if/when you want to support other file types */
const allowedTypes = [
	{
		label: '.pdf',
		type: 'application/pdf',
	},
];

const allowedTypesString = allowedTypes.map((t) => t.label).join(', ');

const FileUploader = (props: Props) => {
	const { onFileSelected, onFileRemoved, onError, file, isUploading } = props;
	const classes = useStyles();
	const [isDropping, setIsDropping] = useState(false);
	const { t } = useTranslation();

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIsDropping(false);
		const files = event.target.files;
		if (files && files.length > 0) {
			const file = files[0];
			if (!allowedTypes.some((t) => t.type === file.type)) {
				onError(
					`${t(
						'shop.supportedFormatsError',
						'The file must be one of the supported formats',
					)} + ': ' ${allowedTypesString}`,
				);
				return;
			}
			const fileName = file.name.replace(/\.[^/.]+$/, '');
			onFileSelected(file, fileName);
		}
	};

	return (
		<Box className={classes.wrapper}>
			{!file ? (
				<Box
					className={classNames(classes.emptyState, {
						[classes.emptyStateDropping]: isDropping,
						[classes.disabled]: props.disabled,
					})}
				>
					<input
						onChange={handleFileSelect}
						onDragOver={(e) => setIsDropping(true)}
						onDragLeave={(e) => setIsDropping(false)}
						className={classes.input}
						type="file"
						accept="application/pdf"
						disabled={props.disabled}
					/>
					{!isUploading ? (
						<Box className={classes.emptyTextWrapper}>
							<Typography variant="button" align="center">
								{isDropping
									? t('shop.dropFileToUpload', 'Release to upload')
									: t('shop.clickOrDragFileToUpload', 'Click or drag file here')}
							</Typography>
							<Typography variant="caption" align="center">
								{t('shop.supportedFormats', 'Supported formats')} {allowedTypesString}
							</Typography>
						</Box>
					) : (
						<Box className={classes.loadingTextWrapper}>
							<Typography className={classes.loadingText} variant="button" align="center">
								{t('common:states.uploading', 'Uploading') + '...'}
							</Typography>
						</Box>
					)}
					<StripePattern hidden={!isUploading} animationSpeed="fast" tint="primary" color="light" />
				</Box>
			) : (
				<Box className={classes.uploadedState}>
					<List>
						<ListItem disabled={props.disabled}>
							<ListItemAvatar>
								<FileIcon />
							</ListItemAvatar>
							<ListItemText
								primary={file.name}
								primaryTypographyProps={{
									className: classes.fileName,
								}}
								secondary={t('shop.viewFile', 'View file')}
								secondaryTypographyProps={{
									component: 'a',
									color: 'primary',
									href: file.url,
									className: classes.fileLink,
									target: '_blank',
									rel: 'noopener noreferrer',
								}}
							/>
							<ListItemSecondaryAction>
								<Tooltip title={t('common:actions.remove')}>
									<IconButton onClick={onFileRemoved} disabled={props.disabled} size="large">
										<ClearIcon />
									</IconButton>
								</Tooltip>
							</ListItemSecondaryAction>
						</ListItem>
					</List>
				</Box>
			)}
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		wrapper: {
			position: 'relative',
			width: '100%',
			borderRadius: '5px',
			overflow: 'hidden',
		},
		disabled: {
			pointerEvents: 'none',
		},
		emptyState: {
			position: 'relative',
			minHeight: '100px',
			padding: theme.spacing(2),
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			cursor: 'pointer',
			transition: 'all 0.2s ease',
			border: `2px solid ${theme.palette.border.inputOutline}`,
			borderRadius: '5px',
			'&:hover, &:focus-within': {
				background: theme.palette.action.hover,
			},
		},
		emptyStateDropping: {
			border: `2px dashed ${theme.palette.border.inputOutline}`,
			background: 'rgba(0, 0, 0, 0.05)',
		},
		emptyTextWrapper: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
		},
		loadingTextWrapper: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			zIndex: 2,
			position: 'absolute',
		},
		loadingText: {
			color: 'white',
			pointerEvents: 'none',
		},
		uploadedState: {
			position: 'relative',
			border: `2px solid ${theme.palette.border.inputOutline}`,
			borderRadius: '5px',
			minHeight: '100px',
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
		},
		fileName: {
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		fileLink: {
			'&:hover': {
				textDecoration: 'underline',
			},
		},
		input: {
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			height: '100%',
			opacity: 0,
			zIndex: 10,
			cursor: 'pointer',
		},
	}),
);

export default FileUploader;
